import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import Sidebar from '../Navigation/Sidebar/Sidebar';
import { toastr } from 'react-redux-toastr';
import { toast } from 'react-toastify';
import SuccessDialog from './SuccessDialog';
import { ArrowBack } from '@mui/icons-material';
import { Link } from 'react-router-dom';

const agents = [
  'sunarsushma73@gmail.com',
  'jiyasocial44@gmail.com',
  'sakshi.gusainn@gmail.com',
  'vishwaassingh8055@gmail.com',
  'simran.chauhan@daalchini.co.in',
  'himanshisinghh121@gmail.com',
  'praveen.kumar@daalchini.co.in'
];

const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const getFullDay = (shortDay) => {
  const mapping = {
    Mon: 'Monday',
    Tue: 'Tuesday',
    Wed: 'Wednesday',
    Thu: 'Thursday',
    Fri: 'Friday',
    Sat: 'Saturday',
    Sun: 'Sunday',
  };
  return mapping[shortDay] || shortDay;
};

const CreateRoster = ({ onPayloadReady, openDialog }) => {
  const [selectedAgent, setSelectedAgent] = useState('');
  const [timeSlots, setTimeSlots] = useState([
    { from: null, to: null, days: [], active: true }
  ]);

  // handle time change
  const handleTimeChange = (index, type, value) => {
    setTimeSlots((prev) =>
      prev.map((slot, i) =>
        i === index ? { ...slot, [type]: value } : slot
      )
    );
  };

  // handle day toggle
  const handleDayToggle = (index, day) => {
    setTimeSlots((prev) =>
      prev.map((slot, i) => {
        if (i !== index) return slot;
        const newDays = slot.days.includes(day)
          ? slot.days.filter((d) => d !== day)
          : [...slot.days, day];
        return { ...slot, days: newDays };
      })
    );
  };

  // handle active toggle
  const handleActiveToggle = (index) => {
    setTimeSlots((prev) =>
      prev.map((slot, i) =>
        i === index ? { ...slot, active: !slot.active } : slot
      )
    );
  };

  // add new slot
  const handleAddSlot = () => {
    setTimeSlots((prev) => [
      ...prev,
      { from: null, to: null, days: [], active: true }
    ]);
  };

  // remove slot
  const handleRemoveSlot = (index) => {
    setTimeSlots((prev) => prev.filter((_, i) => i !== index));
  };

  // validation: at least one valid slot
  const isFormValid = () => {
    return (
      selectedAgent &&
      timeSlots.some(
        (slot) => slot.from && slot.to && slot.days.length > 0
      )
    );
  };

  const isSlotValid = (slot) => {
    return slot.from && slot.to && slot.days.length > 0;
  };

  // save handler
  const handleSave = () => {
    // validate each slot
    for (let i = 0; i < timeSlots.length; i++) {
      if (!isSlotValid(timeSlots[i])) {
        toastr.error(`Please complete slot ${i + 1}`, {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 1000,
        });
        return; // stop saving
      }
    }

    const payload = {
      agentEmail: selectedAgent,
      agentTime: timeSlots.map((slot) => ({
        days: slot.days.map(getFullDay),
        startTime: slot.from ? dayjs(slot.from).format('HH:mm') : null,
        endTime: slot.to ? dayjs(slot.to).format('HH:mm') : null,
        isActive: slot.active,
      })),
    };

    console.log("📦 Final Payload:", payload);

    if (onPayloadReady) {
      onPayloadReady(payload);
    }
  };

  return (
    <div className="main-div">
      <div>
        <Sidebar />
      </div>

      {openDialog && <SuccessDialog />}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box p={4} sx={{ width: "100%" }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            mb={2}
            display="flex"
            alignItems="center"
          >
            <Link to="/cst_rooster">
              <ArrowBack sx={{ color: 'black', mr: 1 }} />
            </Link>
            Create Roster
          </Typography>

          {/* Agent Select */}
          <Typography fontWeight={500} mb={1}>
            Select Agent
          </Typography>
          <Select
            fullWidth
            value={selectedAgent}
            onChange={(e) => setSelectedAgent(e.target.value)}
            displayEmpty
            sx={{ mb: 3 }}
          >
            <MenuItem value="">Select an agent</MenuItem>
            {agents.map((agent) => (
              <MenuItem key={agent} value={agent}>
                {agent}
              </MenuItem>
            ))}
          </Select>

          {/* Time Slots */}
          <Typography fontWeight={500} mb={1}>
            Schedule Time Slots
          </Typography>

          <div style={{ height: "48vh", overflowY: "scroll" }}>
            {timeSlots.map((slot, index) => (
              <Box
                key={index}
                p={5}
                mt={2}
                border="1px solid #eee"
                borderRadius={2}
                position="relative"
                sx={{ backgroundColor: '#fafafa' }}
              >
                {/* Remove Button */}
                {timeSlots.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveSlot(index)}
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}

                {/* Time Pickers */}
                <Box display="flex" gap={2} flexWrap="wrap">
                  <TimePicker
                    label="From"
                    value={slot.from}
                    onChange={(val) => handleTimeChange(index, 'from', val)}
                    renderInput={(params) => <TextField {...params} />}
                  />
                  <TimePicker
                    label="To"
                    value={slot.to}
                    onChange={(val) => handleTimeChange(index, 'to', val)}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </Box>

                {/* Day Chips */}
                <Box mt={2}>
                  <Typography fontSize={14}>Select Days</Typography>
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                    {days.map((day) => (
                      <Chip
                        key={day}
                        label={day}
                        variant={
                          slot.days.includes(day) ? 'filled' : 'outlined'
                        }
                        color={
                          slot.days.includes(day) ? 'primary' : 'default'
                        }
                        onClick={() => handleDayToggle(index, day)}
                        clickable
                      />
                    ))}
                  </Box>
                </Box>

                {/* Active Toggle */}
                <Box mt={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={slot.active}
                        onChange={() => handleActiveToggle(index)}
                        color="success"
                      />
                    }
                    label="Active"
                  />
                </Box>
              </Box>
            ))}
          </div>

          {/* Add Slot */}
          <Box mt={2}>
            <Button
              onClick={handleAddSlot}
              variant="outlined"
              color="success"
            >
              + Add Another Time Slot
            </Button>
          </Box>

          {/* Save */}
          <Box mt={4} textAlign="center">
            <Button
              variant="contained"
              color="primary"
              disabled={!isFormValid()}
              onClick={handleSave}
            >
              Save
            </Button>
          </Box>
        </Box>
      </LocalizationProvider>
    </div>
  );
};

export default CreateRoster;
